import * as React from 'react';

import logger from 'lib/logger';
import { Option } from 'lib/types';
import { Perk } from 'components/loyalty/models/PerkModel';
import { perkApi } from 'components/loyalty/PerkApi';

import { adjustmentSchemesApi } from '../adjustmentSchemesApi';
import { AdjustmentScheme } from '../adjustmentSchemeModel';

interface IProps {
    perks?: Perk[];
    adjustmentSchemes?: AdjustmentScheme[];
}

/**
 *  Returns all the possible discount IDs
 *  Where value is perkId/adjustmentSchemeId and label is {{perkTitle/adjustmentSchemeTitle}} ({{perkId/adjustmentSchemeId})
 */
export const useDiscountIds = (args?: IProps) => {
    const [discountIds, setDiscountIds] = React.useState<Option[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        let isMounted = true;
        async function getAllDiscountIds() {
            try {
                setLoading(true);
                let allAdjustmentSchemes: AdjustmentScheme[] = [];
                let allPerks: Perk[] = [];

                if (!args?.adjustmentSchemes) {
                    const adjustmentSchemesResponse = await adjustmentSchemesApi.getAllSchemes();
                    if (!adjustmentSchemesResponse.ok) {
                        throw new Error('Failed to load adjustment schemes');
                    }
                    allAdjustmentSchemes = [...adjustmentSchemesResponse.items];
                } else {
                    allAdjustmentSchemes = [...args.adjustmentSchemes];
                }

                if (!args?.perks) {
                    const perksResponse = await perkApi.getAllPointPerks();
                    if (!perksResponse.ok) {
                        throw new Error(perksResponse.body.message);
                    }
                    allPerks = [...perksResponse.body.items];
                } else {
                    allPerks = [...args.perks];
                }

                if (isMounted) {
                    setDiscountIds([
                        ...allAdjustmentSchemes
                            .sort((a: AdjustmentScheme, b: AdjustmentScheme) =>
                                a.title.localeCompare(b.title)
                            )
                            .map((scheme: AdjustmentScheme) => ({
                                value: scheme._id,
                                label: `${scheme.title} (${scheme._id})`
                            })),
                        ...allPerks
                            .sort((a: Perk, b: Perk) => a.title.localeCompare(b.title))
                            .map((perk: Perk) => ({
                                value: perk._id,
                                label: `${perk.title || perk.internalTitle} (${perk._id})`
                            }))
                    ]);
                    setLoading(false);
                }
            } catch (error) {
                logger.error(error.message);
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        getAllDiscountIds();
        return () => {
            isMounted = false;
        };
    }, [args?.perks, args?.adjustmentSchemes]);

    return { discountIds, loading };
};
